import { Fragment } from "react";
import type { CostBreakdown } from "@/utils/formulation/computeCost";

type Props = {
  cost: CostBreakdown;
  resolveName: (compoundId: string) => string;
  useMedianFallback: boolean;
  onToggleMedianFallback: (next: boolean) => void;
  medianAvailable: boolean;
};

export function CostReasonsPopover({
  cost, resolveName, useMedianFallback, onToggleMedianFallback, medianAvailable,
}: Props) {
  const unpriced = cost.rows.filter((r) => r.reason === "unpriced");
  const noMw    = cost.rows.filter((r) => r.reason === "no-mw");
  const median  = cost.rows.filter((r) => r.reason === "median");

  return (
    <div
      className="vstack"
      style={{ minWidth: 240, padding: 10, gap: 8, fontSize: 12 }}
    >
      <div className="hstack" style={{ justifyContent: "space-between", gap: 8 }}>
        <span style={{ color: "var(--ink-3)" }}>Priced</span>
        <span className="mono" style={{ color: "var(--ink-2)" }}>{cost.knownCount}</span>
      </div>
      {cost.medianCount > 0 && (
        <div className="hstack" style={{ justifyContent: "space-between", gap: 8 }}>
          <span style={{ color: "var(--ink-3)" }}>Median fallback</span>
          <span className="mono" style={{ color: "var(--ink-2)" }}>{cost.medianCount}</span>
        </div>
      )}
      {unpriced.length > 0 && (
        <Fragment>
          <div className="hstack" style={{ justifyContent: "space-between", gap: 8 }}>
            <span style={{ color: "var(--brand-amber)" }}>Unpriced</span>
            <span className="mono" style={{ color: "var(--ink-2)" }}>{unpriced.length}</span>
          </div>
          <ul style={{ margin: 0, paddingLeft: 14, color: "var(--ink-3)", lineHeight: 1.4 }}>
            {unpriced.map((r) => (
              <li key={r.compoundId}>{resolveName(r.compoundId)}</li>
            ))}
          </ul>
        </Fragment>
      )}
      {noMw.length > 0 && (
        <Fragment>
          <div className="hstack" style={{ justifyContent: "space-between", gap: 8 }}>
            <span style={{ color: "var(--brand-amber)" }}>No MW (mol% rows)</span>
            <span className="mono" style={{ color: "var(--ink-2)" }}>{noMw.length}</span>
          </div>
          <ul style={{ margin: 0, paddingLeft: 14, color: "var(--ink-3)", lineHeight: 1.4 }}>
            {noMw.map((r) => (
              <li key={r.compoundId}>{resolveName(r.compoundId)}</li>
            ))}
          </ul>
        </Fragment>
      )}
      {median.length > 0 && (
        <Fragment>
          <div className="hstack" style={{ justifyContent: "space-between", gap: 8 }}>
            <span style={{ color: "var(--ink-3)" }}>Using median for</span>
            <span className="mono" style={{ color: "var(--ink-2)" }}>{median.length}</span>
          </div>
          <ul style={{ margin: 0, paddingLeft: 14, color: "var(--ink-3)", lineHeight: 1.4 }}>
            {median.map((r) => (
              <li key={r.compoundId}>{resolveName(r.compoundId)}</li>
            ))}
          </ul>
        </Fragment>
      )}
      <label
        className="hstack"
        style={{
          gap: 6, padding: "6px 0 0 0", borderTop: "1px solid var(--line-2)",
          cursor: medianAvailable ? "pointer" : "not-allowed",
          color: medianAvailable ? "var(--ink-2)" : "var(--ink-4)",
        }}
      >
        <input
          type="checkbox"
          checked={useMedianFallback}
          disabled={!medianAvailable}
          onChange={(e) => onToggleMedianFallback(e.currentTarget.checked)}
        />
        <span>Use global median for unpriced rows</span>
      </label>
      {!medianAvailable && (
        <span style={{ fontSize: 10, color: "var(--ink-4)" }}>
          No reference pricing in scope.
        </span>
      )}
    </div>
  );
}
